import moment from 'moment';

export type MeterValue = {
  timestamp: string;
  value: string;
};

export type MeterValues = {
  [measurand: string]: MeterValue[] | undefined;
};

export type ChartPoint = {
  x: string;
  y: number;
};

export type PotencyChartSeries = {
  potency: ChartPoint[];
  soc: ChartPoint[];
  hasSoc: boolean;
};

const POTENCY_MEASURAND = 'Power.Active.Import';

// O backend marca o timestamp como UTC, mas a estação reporta no horário local:
// cortar o sufixo faz o moment interpretar a data como local, como o eixo espera.
const formatTimestamp = (timestamp: string) => {
  const [dateWithoutUTC] = timestamp.match(/^[^.]+/) ?? [timestamp];

  return moment(dateWithoutUTC).format('YYYY-MM-DD HH:mm:ss');
};

/**
 * Number(null), Number('') e Number('  ') devolvem 0 em vez de NaN, então um
 * Number() cru deixaria uma medição ausente entrar no gráfico como um ponto
 * legítimo em zero — o que também engorda o conjunto de valores distintos e
 * habilitaria o segundo eixo de SoC por engano. O tipo diz string, mas contrato
 * não é runtime: tratamos o vazio à parte antes de converter.
 */
const toNumber = (value: string) => {
  if (value == null || String(value).trim() === '') {
    return NaN;
  }

  return Number(value);
};

const byTimestamp = (a: ChartPoint, b: ChartPoint) => a.x.localeCompare(b.x);

/**
 * O backend devolve as medições na ordem em que as gravou, que não é
 * necessariamente a cronológica. O ApexCharts liga os pontos na ordem do array,
 * então um par trocado desenha um zigue-zague em vez da curva. Como
 * formatTimestamp devolve sempre 'YYYY-MM-DD HH:mm:ss', de largura fixa, a
 * ordem lexicográfica coincide com a cronológica.
 */
const toChartPoints = (values: MeterValue[] = []): ChartPoint[] =>
  values
    .map(({ timestamp, value }) => ({
      x: formatTimestamp(timestamp),
      y: toNumber(value),
    }))
    .filter(({ y }) => Number.isFinite(y))
    .sort(byTimestamp);

/**
 * Quando a estação para de reportar SoC no meio da recarga, a curva de SoC
 * morre ali e a de potência segue até o fim — parece que a sessão acabou antes.
 * Repetimos o último valor conhecido num ponto final no instante em que a
 * potência termina: um patamar horizontal, que é repetição e não extrapolação.
 * Repetir o mesmo y também deixa hasSoc intacto, porque valor repetido não
 * entra como valor distinto.
 */
const repeatLastSocUntilPotencyEnds = (
  soc: ChartPoint[],
  potency: ChartPoint[],
): ChartPoint[] => {
  const lastSoc = soc[soc.length - 1];
  const lastPotency = potency[potency.length - 1];

  if (!lastSoc || !lastPotency || byTimestamp(lastSoc, lastPotency) >= 0) {
    return soc;
  }

  return [...soc, { x: lastPotency.x, y: lastSoc.y }];
};

/**
 * Potência e SoC são amostrados de forma independente: numa mesma sessão a
 * estação pode reportar 51 medições de potência e 34 de SoC, em instantes que
 * quase nunca coincidem. Por isso cada série carrega os seus próprios pares
 * (x, y) em vez de compartilhar as categorias do eixo X — reaproveitar os
 * instantes da potência furava a curva de SoC em toda medição sem par e
 * descartava as medições de SoC sem potência no mesmo segundo.
 */
export const buildPotencyChartSeries = (
  values?: MeterValues,
): PotencyChartSeries => {
  const potency = toChartPoints(values?.[POTENCY_MEASURAND]);
  const soc = repeatLastSocUntilPotencyEnds(
    toChartPoints(values?.SoC),
    potency,
  );

  // O SoC só entra no gráfico com pelo menos dois valores distintos. Sem
  // medição nenhuma, ou com o carro repetindo o mesmo valor (tipicamente 0,
  // quando ele não reporta SoC de verdade), sairia uma reta horizontal que não
  // informa nada — e ela custa caro: eixo de porcentagem à direita, mais uma
  // cor na legenda e o eixo de potência trocando a auto-escala pelo
  // enquadramento de buildPotencyYAxisConfiguration. Nesses casos o gráfico
  // segue como era antes do SoC existir: uma curva, um eixo.
  const hasSoc = new Set(soc.map(({ y }) => y)).size > 1;

  return { potency, soc, hasSoc };
};
